import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface ConnectedDotsBackgroundProps {
  /** Number of moving dots. Scaled down automatically on small screens. */
  dotCount?: number;
  /** Movement speed multiplier. */
  speed?: number;
  /** Maximum distance (px) between two dots for a connecting line to be drawn. */
  linkDistance?: number;
  /** Radius (px) of each dot. */
  dotRadius?: number;
  /** Width (px) of the connecting lines. */
  lineWidth?: number;
  /** CSS color string for dots and lines (e.g. theme.palette.secondary.main). */
  color?: string;
  /** CSS background color/gradient painted behind the animation. */
  background?: string;
  /** When true, dots gently react to the pointer position. */
  interactive?: boolean;
}

interface Dot {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const hexToRgb = (color: string): [number, number, number] => {
  const hex = color.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map((c) => c + c).join('')
    : hex;
  const int = parseInt(normalized, 16);
  if (Number.isNaN(int) || normalized.length !== 6) return [120, 144, 156];
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
};

/**
 * Full-bleed animated "particle network" background: dots drift across the
 * canvas and draw fading lines to nearby neighbours. Rendered behind page
 * content via an absolutely positioned canvas.
 */
export const ConnectedDotsBackground: React.FC<ConnectedDotsBackgroundProps> = ({
  dotCount = 90,
  speed = 0.5,
  linkDistance = 140,
  dotRadius = 2.4,
  lineWidth = 1,
  color = '#90a4ae',
  background = 'transparent',
  interactive = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const dotsRef = useRef<Dot[]>([]);
  const pointerRef = useRef<{ x: number; y: number; active: boolean }>({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const [r, g, b] = hexToRgb(color);
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    let width = 0;
    let height = 0;

    const resolveDotCount = () => {
      const area = width * height;
      // Keep density consistent: roughly one dot per ~12k css px².
      const densityCount = Math.round(area / 12000);
      return Math.max(24, Math.min(dotCount, densityCount));
    };

    const seedDots = () => {
      const count = resolveDotCount();
      dotsRef.current = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
      }));
    };

    const resize = () => {
      const parent = canvas.parentElement;
      width = parent ? parent.clientWidth : window.innerWidth;
      height = parent ? parent.clientHeight : window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedDots();
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const dots = dotsRef.current;
      const pointer = pointerRef.current;
      const linkDistanceSq = linkDistance * linkDistance;

      for (let i = 0; i < dots.length; i++) {
        const dot = dots[i];

        if (!prefersReducedMotion) {
          dot.x += dot.vx;
          dot.y += dot.vy;
          if (dot.x <= 0 || dot.x >= width) dot.vx *= -1;
          if (dot.y <= 0 || dot.y >= height) dot.vy *= -1;
          dot.x = Math.max(0, Math.min(width, dot.x));
          dot.y = Math.max(0, Math.min(height, dot.y));
        }

        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dotRadius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.85)`;
        ctx.fill();

        for (let j = i + 1; j < dots.length; j++) {
          const other = dots[j];
          const dx = dot.x - other.x;
          const dy = dot.y - other.y;
          const distSq = dx * dx + dy * dy;
          if (distSq < linkDistanceSq) {
            const opacity = (1 - distSq / linkDistanceSq) * 0.55;
            ctx.beginPath();
            ctx.moveTo(dot.x, dot.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
            ctx.lineWidth = lineWidth;
            ctx.stroke();
          }
        }

        if (interactive && pointer.active) {
          const dxp = dot.x - pointer.x;
          const dyp = dot.y - pointer.y;
          const distSqP = dxp * dxp + dyp * dyp;
          if (distSqP < linkDistanceSq) {
            const opacity = (1 - distSqP / linkDistanceSq) * 0.7;
            ctx.beginPath();
            ctx.moveTo(dot.x, dot.y);
            ctx.lineTo(pointer.x, pointer.y);
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
            ctx.lineWidth = lineWidth;
            ctx.stroke();
          }
        }
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    const handlePointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointerRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top, active: true };
    };
    const handlePointerLeave = () => {
      pointerRef.current.active = false;
    };

    resize();
    draw();

    window.addEventListener('resize', resize);
    if (interactive) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerout', handlePointerLeave);
    }

    return () => {
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resize);
      if (interactive) {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerout', handlePointerLeave);
      }
    };
  }, [dotCount, speed, linkDistance, dotRadius, lineWidth, color, interactive]);

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        background,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </Box>
  );
};

export default ConnectedDotsBackground;
